import axios from 'axios';
import { getAuthToken, getToken, logout } from '../common/routines/user-operations';
import { DOWNLOAD_HOST, sourceHeaderValue } from '../constants/axios';
import { LOG_IN_AGAIN } from '../constants/errors';
import store from '../store';
import { networkErrorHandler, warningHandler } from '../utils/handlers';
import { isAzureMfaEnabled } from './env';

const getApiBaseUrl = (serverState) => {
  const port = serverState.serverPort ? `:${serverState.serverPort}` : '';
  return `${serverState.serverProtocol}://${serverState.serverUrl}${port}/api/v1`;
};

const setupAxios = () => {
  const appState = store.getState();

  const baseURL = getApiBaseUrl(appState.server);
  const instance = axios.create({
    baseURL,
  });

  instance.interceptors.request.use(async (config) => {
    const customConfig = config;
    const appServerState = store.getState();
    const appBaseURL = getApiBaseUrl(appServerState.server);
    customConfig.baseURL = appBaseURL;
    customConfig.headers = {
      'Content-Type': 'application/json',
    };
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (config.url !== '/login' && !config.url.includes(DOWNLOAD_HOST)) {
      if (isAzureMfaEnabled?.toLowerCase() === 'true') {
        try {
          const authToken = await getAuthToken();
          if (authToken) {
            customConfig.headers.Authorization = authToken;
            customConfig.headers.role = user.userRole;
          } else {
            console.debug('No MSAL auth token available; skipping Authorization header');
          }
        } catch (err) {
          console.error('getAuthToken failed:', err);
        }
      } else {
        try {
          const token = await getToken();
          if (token) customConfig.headers.Authorization = token;
        } catch (err) {
          console.error('getToken failed:', err);
        }
      }
      customConfig.headers.source = sourceHeaderValue;
    }

    // added a 800 millisecond delay to solve the issue of not getting quick updated responses as read replica is not being updated
    if (customConfig?.method === 'get') await sleep(800);

    return customConfig;
  });

  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // 401 handling: retry once with a fresh token before giving up
      if (error?.response?.status === 401) {
        // If the backend explicitly says the user must re-login (e.g. role change),
        // don't retry — go straight to logout with a message.
        if (error?.response?.data?.message === LOG_IN_AGAIN) {
          logout();
          warningHandler('Your role was changed recently. Please log in again', {
            container: 'top-right',
            dismiss: { duration: 3000 },
          });
          return Promise.reject(error);
        }

        // Only retry once — check the flag to prevent infinite loops
        if (!originalRequest._authRetried) {
          originalRequest._authRetried = true;

          try {
            const freshToken = await getAuthToken({ forceRefresh: true });
            if (freshToken) {
              originalRequest.headers.Authorization = freshToken;
              return instance(originalRequest);
            }
          } catch (retryError) {
            console.error('Token refresh failed during 401 retry:', retryError);
          }
        }

        // Retry failed or no token — logout
        console.log('auth failed after retry');
        logout();
        return Promise.reject(error);
      }

      if (error?.message === 'Network Error') {
        networkErrorHandler();
      }

      return Promise.reject(error);
    },
  );

  return instance;
};

function sleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

export default setupAxios;
